import { TextareaAutosize } from "@material-ui/core";
import Collapse from "@material-ui/core/Collapse";
import AppContext from "context/AppContext";
import firebase from "firebase/app";
import orderBy from "lodash/orderBy";
import React, {
  FormEvent,
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react";
import { Errors } from "shared/types/errors";
import apiCalls from "shared/utils/apiCalls";
import wrapLocalLoader from "shared/utils/wrapLocalLoader";
import Button from "../Buttons/Button";
import ErrorMessage from "../ErrorMessage/ErrorMessage";
import Modal from "../Modal/Modal";
import Feedback from "./Feedback/Feedback";
import styles from "./Feedbacks.module.scss";
import FeedbacksSkeleton from "./FeedbacksSkeleton/FeedbacksSkeleton";

const Feedbacks: FunctionComponent<{
  parentId: string;
  challengeId: string;
  solution: any;
}> = ({ parentId, challengeId, solution }) => {
  const [feedbacks, setFeedbacks] = useState<any[]>();
  const [feedback, setFeedback] = useState<string>();
  const [feedbackLoading, setFeedbackLoading] = useState<boolean>();
  const { state, dispatch } = useContext(AppContext);

  const [
    permissionDeclinedModalOpen,
    setPermissionDeclinedModalOpen,
  ] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!state.user) {
      return;
    }

    if (state.user.reputations < 0) {
      setPermissionDeclinedModalOpen(true);
      return;
    }

    if (!challengeId) {
      return;
    }

    const submit = wrapLocalLoader(async () => {
      const res = await apiCalls(
        {
          method: "post",
          data: {
            content: feedback,
            parentId,
            challengeId,
            targetUid: solution.uid,
          },
          url: `/feedbacks`,
        },
        dispatch
      );

      firebase.analytics().logEvent("feedback_given");

      setFeedback("");

      if (res) {
        await getFeedbacks();
      }
    }, setFeedbackLoading);

    submit();
  };

  const getFeedbacks = async () => {
    const res = await apiCalls(
      {
        method: "get",
        url: `/feedbacks/solutions/${solution.id}`,
      },
      dispatch
    );

    if (res) {
      setFeedbacks(orderBy(res.data, "submittedAt"));
    }
  };

  useEffect(() => {
    getFeedbacks();
    // eslint-disable-next-line
  }, []);

  if (!feedbacks) {
    return <FeedbacksSkeleton />;
  }

  return (
    <div className={styles.feedbacks}>
      <Modal
        open={permissionDeclinedModalOpen}
        closeModal={() => setPermissionDeclinedModalOpen(false)}
        size="sm"
      >
        <ErrorMessage errorType={Errors.reputationsDeclined} />
      </Modal>

      <h2>Feedbacks</h2>

      {(!feedbacks || feedbacks.length <= 0) && (
        <div className={styles.feedbacks_noFeedback}>No feedback found</div>
      )}

      {feedbacks.map((feedback) => (
        <Feedback
          key={feedback.id}
          feedback={feedback}
          getFeedbacks={getFeedbacks}
        />
      ))}

      {state.user && (
        <form onSubmit={onSubmit}>
          <TextareaAutosize
            onChange={(e: any) => setFeedback(e.target.value)}
            value={feedback}
            className={styles.feedbacks_textInput}
            name="feedback"
            aria-label="feedback"
            rowsMin={5}
            placeholder="Feedback"
          />
          <div className={styles.feedbacks_helperText}>
            You can use Markdown to write and format your feedback.
          </div>

          <Collapse in={!!feedback}>
            <div className={styles.feedbacks_sendButton}>
              <Button loading={feedbackLoading} type="submit" aria-label="send">
                Send
              </Button>
            </div>
          </Collapse>
        </form>
      )}
    </div>
  );
};

export default Feedbacks;
